import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiService } from '@/services/api'

export default function CampaignNew() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    channel: 'Display', // Default
    customerType: 'DTC', // Default
    indication: '',
    therapeuticArea: '',
    brand: '',
    product: '',
    fdaFilingNumber: '',
    dofuExpiration: ''
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [expandedSections, setExpandedSections] = useState({
    details: false,
    product: false,
    compliance: false
  })

  const toggleSection = (section: 'details' | 'product' | 'compliance') => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const newErrors: Record<string, string> = {}
    if (!formData.name.trim()) {
      newErrors.name = 'Campaign name is required'
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setLoading(true)
    setErrors({})
    try {
      const response = await apiService.request('/api/campaigns', {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          status: 'draft',
          assetCount: 0,
          complianceScore: 0
        })
      })
      
      console.log('✅ Campaign creation response:', response)
      
      // Check multiple possible response structures
      const campaignId = response.campaign?.id || response.data?.campaign?.id || response.data?.campaignId || response.data?.id
      
      if (campaignId) {
        console.log(`✅ Campaign created: ${campaignId}, navigating...`)
        // Show success message briefly before redirecting
        setErrors({ submit: '✅ Campaign created successfully! Redirecting...' })
        setTimeout(() => {
          navigate(`/campaigns/${campaignId}`)
        }, 500)
      } else {
        console.warn('⚠️  Campaign created but no ID returned:', response)
        // Still redirect to campaigns list
        setErrors({ submit: '✅ Campaign created! Redirecting to campaigns...' })
        setTimeout(() => {
          navigate('/campaigns')
        }, 1000)
      }
    } catch (error) {
      console.error('❌ Failed to create campaign:', error)
      setErrors({ submit: 'Failed to create campaign. Please try again.' })
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/campaigns')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Campaigns
          </Button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Create New Campaign</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Set up a new pharmaceutical marketing campaign with compliance tracking
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Campaign Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Q4 2025 Diabetes Campaign"
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && <p className="text-sm text-red-500 mt-1">{errors.name}</p>}
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Describe the campaign objectives, target audience, and key messaging..."
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          {/* Campaign Details */}
          <Card>
            <CardHeader 
              className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              onClick={() => toggleSection('details')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CardTitle>Campaign Details</CardTitle>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Optional</span>
                </div>
                {expandedSections.details ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </div>
            </CardHeader>
            {expandedSections.details && (
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="channel">Channel</Label>
                    <Select value={formData.channel} onValueChange={(value) => handleChange('channel', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select channel" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Display">Display</SelectItem>
                        <SelectItem value="OLV">OLV (Online Video)</SelectItem>
                        <SelectItem value="CTV">CTV (Connected TV)</SelectItem>
                        <SelectItem value="YouTube">YouTube</SelectItem>
                        <SelectItem value="Meta">Meta (Facebook/Instagram)</SelectItem>
                        <SelectItem value="Programmatic">Programmatic</SelectItem>
                        <SelectItem value="Email">Email</SelectItem>
                        <SelectItem value="Print">Print</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="customerType">Customer Type</Label>
                    <Select value={formData.customerType} onValueChange={(value) => handleChange('customerType', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DTC">DTC (Direct to Consumer)</SelectItem>
                        <SelectItem value="HCP">HCP (Healthcare Professional)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="indication">Indication</Label>
                    <Input
                      id="indication"
                      value={formData.indication}
                      onChange={(e) => handleChange('indication', e.target.value)}
                      placeholder="Type 2 Diabetes"
                    />
                  </div>

                  <div>
                    <Label htmlFor="therapeuticArea">Therapeutic Area</Label>
                    <Input
                      id="therapeuticArea"
                      value={formData.therapeuticArea}
                      onChange={(e) => handleChange('therapeuticArea', e.target.value)}
                      placeholder="Endocrinology"
                    />
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Product Information */}
          <Card>
            <CardHeader 
              className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              onClick={() => toggleSection('product')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CardTitle>Product Information</CardTitle>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Optional</span>
                </div>
                {expandedSections.product ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </div>
            </CardHeader>
            {expandedSections.product && (
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="brand">Brand</Label>
                    <Input
                      id="brand"
                      value={formData.brand}
                      onChange={(e) => handleChange('brand', e.target.value)}
                      placeholder="GlucoCare"
                    />
                  </div>

                  <div>
                    <Label htmlFor="product">Product</Label>
                    <Input
                      id="product"
                      value={formData.product}
                      onChange={(e) => handleChange('product', e.target.value)}
                      placeholder="GlucoCare XR"
                    />
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Compliance Information */}
          <Card>
            <CardHeader 
              className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              onClick={() => toggleSection('compliance')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CardTitle>Compliance</CardTitle>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Optional</span>
                </div>
                {expandedSections.compliance ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </div>
            </CardHeader>
            {expandedSections.compliance && (
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="fdaFilingNumber">FDA Filing Number</Label>
                    <Input
                      id="fdaFilingNumber"
                      value={formData.fdaFilingNumber}
                      onChange={(e) => handleChange('fdaFilingNumber', e.target.value)}
                      placeholder="PP-ABC-USA-1234"
                    />
                  </div>

                  <div>
                    <Label htmlFor="dofuExpiration">DOFU Expiration</Label>
                    <Input
                      id="dofuExpiration"
                      type="date"
                      value={formData.dofuExpiration}
                      onChange={(e) => handleChange('dofuExpiration', e.target.value)}
                    />
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Submit Error */}
          {errors.submit && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pb-6">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate('/campaigns')}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Campaign'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

